import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Lockers from './pages/Lockers';
import Leases from './pages/Leases';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Products from './pages/Products';
import LockerDetails from './pages/LockerDetails';
import ShowLease from './pages/ShowLease';
import Stocks from './pages/Stocks';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminManagement from './pages/AdminManagement';
import ChangePassword from './pages/ChangePassword';
import Support from './pages/Support';
import { AuthProvider, useAuth } from './AuthContext';
import { ToastContainer } from 'react-toastify';
import EditMachine from './pages/EditMachine';
import EditProduct from './pages/EditProduct';
import EditOrganizationInfo from './pages/EditOrganizationInfo';
import CellTransactionHistory from './pages/CellTransactionHistory';
import DoorStatusHistoryPage from './pages/DoorStatusHistoryPage';
import { GlobalProvider } from './GlobalContext';
import LockerActuators from './pages/LockerActautors';
import VirtualProducts from './pages/VirtualProducts';
import PaymentSettings from './pages/PaymentSettings';
import PendingFinalisations from './pages/PendingFinalisations';

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { token, organizationInfo, loading } = useAuth();
  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }
  return token && organizationInfo ? element : <Navigate to="/login" />;
};

const AppLayout: React.FC = () => {
  const { token } = useAuth(); // Now safe, inside AuthProvider
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleSidebar = () => setIsOpen(!isOpen);
  const handleLinkClick = () => setIsOpen(false);

  return (
    <div className="flex flex-col h-screen">
      <Header onMenuToggle={toggleSidebar} />
      <div className="flex">
        {token && <Sidebar isOpen={isOpen} onClose={handleLinkClick} />}
        <main className={`flex-1 overflow-y-auto mt-16 ${token ? 'lg:ml-64' : 'ml-0'} transition-all duration-300`}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/support" element={<Support />} />
            <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
            <Route path="/" element={<ProtectedRoute element={<Dashboard />} />} />
            <Route path="/lockers" element={<ProtectedRoute element={<Lockers />} />} />
            <Route path="/leases" element={<ProtectedRoute element={<Leases />} />} />
            <Route path="/pending-finalisations" element={<ProtectedRoute element={<PendingFinalisations />} />} />
            <Route path="/customers" element={<ProtectedRoute element={<Customers />} />} />
            <Route path="/customer/:customerUuid" element={<ProtectedRoute element={<CustomerDetail />} />} />
            <Route path="/products" element={<ProtectedRoute element={<Products />} />} />
            <Route path="/edit-product/:productUuid" element={<ProtectedRoute element={<EditProduct />} />} />
            <Route path="/locker-details/:machineUuid" element={<ProtectedRoute element={<LockerDetails />} />} />
            <Route path="/manage-actuators/:machineUuid" element={<ProtectedRoute element={<LockerActuators />} />} />
            <Route path="/show-lease/:customerUuid" element={<ProtectedRoute element={<ShowLease />} />} />
            <Route path="/edit-organization-info" element={<ProtectedRoute element={<EditOrganizationInfo />} />} />
            <Route path="/stocks/:productUuid" element={<ProtectedRoute element={<Stocks />} />} />
            <Route path="/edit-machine/:machineUuid" element={<ProtectedRoute element={<EditMachine />} />} />
            <Route path="/payment-settings/:machineUuid" element={<ProtectedRoute element={<PaymentSettings />} />} />
            <Route path="/cell-transactions/:machineUuid/:mdbItemNumber" element={<ProtectedRoute element={<CellTransactionHistory />} />} />
            <Route path="/door-status-history" element={<ProtectedRoute element={<DoorStatusHistoryPage />} />} />
            <Route path="/door-status-history/:machineUuid" element={<ProtectedRoute element={<DoorStatusHistoryPage />} />} />
            <Route path="/door-status-history/:machineUuid/:mdbItemNumber" element={<ProtectedRoute element={<DoorStatusHistoryPage />} />} />
            <Route path="/admin-management" element={<ProtectedRoute element={<AdminManagement />} />} />
            <Route path="/virtual-products" element={<ProtectedRoute element={<VirtualProducts />} />} />
            <Route path="/change-password" element={<ProtectedRoute element={<ChangePassword />} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <AuthProvider>
        <GlobalProvider>
          <Router>
            <AppLayout />
          </Router>
        </GlobalProvider>
      </AuthProvider>
    </>
  );
};

export default App;
